'use client';

import { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from 'react';
import {
    Users,
    CheckCircle2,
    XCircle,
    ChevronDown,
    ChevronRight,
    Clock,
    Filter,
    ExternalLink,
    X,
    Footprints,
    Search,
    Loader2,
    Tag,
    Smartphone,
    Globe2,
    Wifi,
    FlaskConical,
    ArrowUpDown,
    Split,
    SlidersHorizontal,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';
import { DeviceTypeIcon, BrowserIcon, OSIcon, isInAppBrowser } from '@/components/ui/device-icons';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { getEventLabel, isConversionEvent, isErrorEvent } from '@/lib/constants/app-events';
import { clampAnalyticsRange } from './analyticsRange';

// Custom hook for debounced value
function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Country flag emoji from country code (ISO 3166-1 alpha-2)
function getCountryFlag(countryCode: string | null): string {
    if (!countryCode || countryCode.length !== 2) return '';
    try {
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    } catch {
        return '';
    }
}

// Local type definitions matching backend (to work around TRPC type inference lag)
interface FunnelSessionEvent {
    id: string;
    eventType: string;
    createdAt: string;
    stepId: string | null;
    stepType: string | null;
    stepName: string | null;
    /** Variant ID if this event is part of an A/B test step */
    variantId?: string | null;
    /** Human-readable variant label (e.g., "Variant A", "Control") */
    variantLabel?: string | null;
}

// Customer trace event type - simplified for timeline display
interface CustomerTraceEvent {
    id: string;
    eventType: string;
    createdAt: string;
    stepId?: string | null;
    stepType?: string | null;
    /** Step name (more descriptive than stepType) */
    stepName?: string | null;
    funnelSessionId?: string | null;
    checkoutSessionId?: string | null;
    orderId?: string | null;
    paymentId?: string | null;
    amount?: number | null;
    currency?: string | null;
    status?: string | null;
    subStatus?: string | null;
    isPreview?: boolean;
    /** Variant ID if this event is part of an A/B test step */
    variantId?: string | null;
    /** Human-readable variant label (e.g., "A", "B") */
    variantLabel?: string | null;
}

interface FunnelSessionTrace {
    sessionId: string;
    customerId: string | null;
    customerEmail: string | null;
    customerTags: string[];
    status: 'converted' | 'dropped';
    dropOffStepId: string | null;
    lastEventType: string | null;
    deviceType: string | null;
    browser: string | null;
    os: string | null;
    country: string | null;
    startedAt: string;
    endedAt: string;
    durationSeconds: number;
    eventCount: number;
    events: FunnelSessionEvent[];
    /** Whether this session was initiated from preview mode */
    isPreview: boolean;
    /** Whether this session includes any A/B test steps */
    hasSplitTests?: boolean;
    /** Map of stepId -> variant info for steps that are split tests */
    stepVariants?: Record<string, { variantId: string; variantLabel: string | null }>;
}

interface FunnelSessionTracesResult {
    traces: FunnelSessionTrace[];
    total: number;
    hasMore: boolean;
    summary: {
        totalSessions: number;
        convertedSessions: number;
        droppedSessions: number;
        conversionRate: number;
    };
    stepNameMap: Record<string, string>;
}

// Sub-component for expanded session content with customer trace
function ExpandedSessionContent({
    session,
    storeId,
    orgRoutes,
}: {
    session: FunnelSessionTrace;
    storeId: string;
    orgRoutes: ReturnType<typeof useOrgNavigate>['orgRoutes'];
}) {
    // Fetch full customer trace when expanded - match the working customer page query
    const { data: customerTrace, isFetching } = trpc.analytics.customerTrace.useQuery(
        {
            customerId: session.customerId!,
            limit: 200,
            excludeDraft: false,
        },
        {
            enabled: !!session.customerId,
            staleTime: 30000, // Cache for 30s
        }
    );
    
    // Get events from response
    const events = customerTrace?.events;
    const hasEvents = events && events.length > 0;

    return (
        <div className="mt-1 ml-5 p-2.5 bg-muted/20 rounded-md border border-border/50 space-y-2.5">
            {/* Session Meta */}
            <div className="flex flex-wrap items-center gap-3 text-[11px]">
                {session.customerId && (
                    <a
                        href={orgRoutes.CUSTOMER_DETAILS(session.customerId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline font-medium"
                    >
                        <ExternalLink className="h-3 w-3" />
                        View Customer
                    </a>
                )}
                {session.country && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                        <span className="text-sm">{getCountryFlag(session.country)}</span>
                        {session.country}
                    </span>
                )}
                {session.os && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                        <OSIcon os={session.os} className="h-3 w-3" />
                        {session.os}
                    </span>
                )}
                {session.browser && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                        <BrowserIcon browser={session.browser} className="h-3 w-3" />
                        {session.browser}
                    </span>
                )}
                <span className="text-muted-foreground/60 text-[10px] font-mono">
                    {session.sessionId.slice(-12)}
                </span>
            </div>
            
            {/* Customer Tags */}
            {session.customerTags && session.customerTags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <Tag className="h-3 w-3 text-muted-foreground" />
                    {session.customerTags.map((tag) => (
                        <Badge 
                            key={tag} 
                            variant="outline" 
                            className="h-5 px-1.5 text-[10px] font-normal"
                        >
                            {tag}
                        </Badge>
                    ))}
                </div>
            )}

            {/* Customer Event Trace */}
            <div>
                <p className="text-[10px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Customer Timeline
                    {isFetching && <Loader2 className="inline-block h-3 w-3 ml-1.5 animate-spin" />}
                </p>
                
                {hasEvents ? (
                    <div className="relative pl-3 border-l border-border/60 space-y-1 max-h-[300px] overflow-y-auto">
                        {events.map((event: CustomerTraceEvent) => {
                            const isPaid = isConversionEvent(event.eventType);
                            const isError = isErrorEvent(event.eventType);
                            
                            return (
                                <div 
                                    key={event.id} 
                                    className="relative py-0.5"
                                >
                                    <div className={cn(
                                        'absolute w-1.5 h-1.5 rounded-full top-2 -left-[4px]',
                                        isPaid ? 'bg-green-500' : isError ? 'bg-red-500' : 'bg-muted-foreground/40'
                                    )} />
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className={cn(
                                            'text-[11px] font-mono',
                                            isPaid && 'text-green-600 font-medium',
                                            isError && 'text-red-600'
                                        )}>
                                            {getEventLabel(event.eventType)}
                                            {/* Show step name (prefer stepName over stepType) */}
                                            {(event.stepName || event.stepType) && (
                                                <span className="text-muted-foreground/70 font-normal ml-1">
                                                    → {event.stepName || event.stepType}
                                                </span>
                                            )}
                                            {event.amount != null && (
                                                <span className="text-green-600 font-medium ml-1">
                                                    {(event.amount / 100).toFixed(2)} {event.currency || ''}
                                                </span>
                                            )}
                                            {event.status && !isPaid && (
                                                <Badge 
                                                    variant={event.status === 'paid' || event.status === 'completed' ? 'green' : 
                                                             event.status === 'failed' || event.status === 'declined' ? 'red' : 'secondary'}
                                                    className="ml-1.5 h-3.5 px-1 text-[8px]"
                                                >
                                                    {event.status}
                                                </Badge>
                                            )}
                                            {event.isPreview && (
                                                <Badge variant="yellow" className="ml-1.5 h-3.5 px-1 text-[8px]">
                                                    Preview
                                                </Badge>
                                            )}
                                            {event.variantId && (
                                                <Badge variant="purple" className="ml-1.5 h-3.5 gap-0.5 px-1 text-[8px]">
                                                    <Split className="h-2 w-2" />
                                                    {event.variantLabel || event.variantId.split('_').pop() || 'Variant'}
                                                </Badge>
                                            )}
                                        </span>
                                        <span className="text-[9px] text-muted-foreground/60 tabular-nums flex-shrink-0">
                                            {format(new Date(event.createdAt), 'MMM d HH:mm:ss')}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-[10px] text-muted-foreground/60">No events found</p>
                )}
            </div>
        </div>
    );
}

// Removable chip summarising one active filter — lets the toolbar stay a single
// line (search + Filters popover) while still surfacing what's applied.
function FilterChip({ icon, label, onClear }: { icon?: ReactNode; label: string; onClear: () => void }) {
    return (
        <span className="inline-flex h-6 items-center gap-1 rounded-full bg-muted pl-2 pr-1 text-xs text-foreground">
            {icon}
            <span className="max-w-[150px] truncate">{label}</span>
            <button
                type="button"
                onClick={onClear}
                className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
                aria-label={`Remove ${label} filter`}
            >
                <X className="h-3 w-3" />
            </button>
        </span>
    );
}

interface SessionTracesPanelProps {
    funnelId: string;
    storeId: string;
    steps: { id: string; name: string; type: string }[];
    dateRange: { from: Date; to: Date };
    environment: 'staging' | 'production';
    className?: string;
    /** Pre-selected step ID (from canvas click) */
    selectedStepId?: string | null;
    /** Filter to a specific A/B variant ID */
    variantId?: string | null;
    /** Callback when step filter changes */
    onStepSelect?: (stepId: string | null) => void;
}

type SessionStatus = 'all' | 'converted' | 'dropped';

/**
 * Session Traces Panel - Shows all funnel sessions with their journey details
 * 
 * Features:
 * - Filter by status (all/converted/dropped)
 * - Filter by step visited (sessions that reached a specific step)
 * - Click on step in canvas to auto-filter sessions
 */
export function SessionTracesPanel({
    funnelId,
    storeId,
    steps,
    dateRange,
    environment,
    className,
    selectedStepId: externalStepId,
    variantId: externalVariantId,
    onStepSelect,
}: SessionTracesPanelProps) {
    const [statusFilter, setStatusFilter] = useState<SessionStatus>('all');
    const [stepFilter, setStepFilter] = useState<string | null>(externalStepId || null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deviceTypeFilter, setDeviceTypeFilter] = useState<string | null>(null);
    const [countryFilter, setCountryFilter] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<'started_at' | 'ended_at'>('started_at');
    const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
    const [page, setPage] = useState(0);

    // Clamp the raw UI range (which may be the "All time" epoch→far-future
    // sentinel) to the server's max analytics span before querying.
    const queryRange = useMemo(
        () => clampAnalyticsRange(dateRange.from, dateRange.to),
        [dateRange.from, dateRange.to],
    );
    const pageSize = 50; // Increased page size since backend handles it efficiently
    
    // Smart polling state
    const [isPollingEnabled, setIsPollingEnabled] = useState(true);
    const pollingInterval = 5000; // 5 seconds
    
    // Track known session IDs to detect new ones
    const knownSessionIds = useRef<Set<string>>(new Set());
    const [newSessionIds, setNewSessionIds] = useState<Set<string>>(new Set());

    const { orgRoutes } = useOrgNavigate();

    // Debounce search to avoid too many backend requests
    const debouncedSearch = useDebouncedValue(searchQuery, 300);

    // Reset pagination when filters change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, deviceTypeFilter, countryFilter]);

    // Sync external step selection with internal filter
    useEffect(() => {
        if (externalStepId !== undefined) {
            setStepFilter(externalStepId);
            setPage(0);
        }
    }, [externalStepId]);

    // Handle step filter change
    const handleStepFilterChange = useCallback((value: string) => {
        const newStepId = value === 'all' ? null : value;
        setStepFilter(newStepId);
        setPage(0);
        onStepSelect?.(newStepId);
    }, [onStepSelect]);

    // Clear step filter
    const clearStepFilter = useCallback(() => {
        setStepFilter(null);
        setPage(0);
        onStepSelect?.(null);
    }, [onStepSelect]);

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        setStatusFilter('all');
        setStepFilter(null);
        setSearchQuery('');
        setDeviceTypeFilter(null);
        setCountryFilter(null);
        setPage(0);
        onStepSelect?.(null);
    }, [onStepSelect]);

    // Check if any filter is active
    const hasActiveFilters = statusFilter !== 'all' || stepFilter || searchQuery || deviceTypeFilter || countryFilter;

    // Count filters tucked inside the Filters popover (search lives in its own
    // always-visible field, so it's excluded from the badge count).
    const popoverFilterCount =
        (statusFilter !== 'all' ? 1 : 0) +
        (stepFilter ? 1 : 0) +
        (deviceTypeFilter ? 1 : 0) +
        (countryFilter ? 1 : 0);

    // Fetch session traces - ALL filtering done server-side
    // Smart polling enabled on first page to catch new sessions
    const {
        data: rawTracesData,
        isLoading,
        isFetching,
    } = trpc.analytics.funnelSessionTraces.useQuery(
        {
            funnelId,
            storeIds: [storeId],
            startDate: queryRange.from,
            endDate: queryRange.to,
            environment,
            status: statusFilter,
            reachedStepId: stepFilter || undefined,
            variantId: externalVariantId || undefined,
            search: debouncedSearch || undefined,
            deviceType: deviceTypeFilter || undefined,
            country: countryFilter || undefined,
            sortBy,
            limit: pageSize,
            offset: page * pageSize,
        } as Parameters<typeof trpc.analytics.funnelSessionTraces.useQuery>[0],
        {
            staleTime: 5000, // Shorter stale time for live updates
            // Only poll on first page with no search filter (most likely to see new sessions)
            refetchInterval: isPollingEnabled && page === 0 && !debouncedSearch ? pollingInterval : false,
        }
    );
    
    // Detect new sessions and mark them for animation
    useEffect(() => {
        if (!rawTracesData?.traces) return;
        
        const currentIds = new Set(rawTracesData.traces.map((t: any) => t.sessionId));
        const newIds = new Set<string>();
        
        // On first load, store all IDs as known (no animation)
        if (knownSessionIds.current.size === 0) {
            knownSessionIds.current = currentIds;
            return;
        }
        
        // Find truly new sessions (not in our known set)
        for (const id of currentIds) {
            if (!knownSessionIds.current.has(id)) {
                newIds.add(id);
                knownSessionIds.current.add(id);
            }
        }
        
        if (newIds.size > 0) {
            setNewSessionIds(newIds);
            // Clear animation state after 3 seconds
            setTimeout(() => {
                setNewSessionIds(new Set());
            }, 3000);
        }
    }, [rawTracesData?.traces]);
    
    // Cast to our local types for type safety
    const tracesData = rawTracesData as FunnelSessionTracesResult | undefined;

    // Step name lookup
    const stepNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        steps.forEach((step) => {
            map[step.id] = step.name;
        });
        return map;
    }, [steps]);

    // Toggle session expansion
    const toggleSession = (sessionId: string) => {
        setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
    };

    // Format duration
    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins < 60) return `${mins}m ${secs}s`;
        const hours = Math.floor(mins / 60);
        return `${hours}h ${mins % 60}m`;
    };

    // Extract data from query result (must be before any early returns to maintain hook order)
    const { traces: rawTraces, summary, total: rawTotal, hasMore, stepNameMap: backendStepNameMap } = tracesData || {
        traces: [],
        summary: { totalSessions: 0, convertedSessions: 0, droppedSessions: 0, conversionRate: 0 },
        total: 0,
        hasMore: false,
        stepNameMap: {},
    };

    // Merge backend step names with frontend step names (backend takes priority)
    const mergedStepNameMap = useMemo(() => ({
        ...stepNameMap,
        ...backendStepNameMap,
    }), [stepNameMap, backendStepNameMap]);

    // Use backend-provided data directly (search is done server-side)
    const traces = rawTraces;
    const total = rawTotal;

    // Get the selected step name for display
    const selectedStepName = stepFilter ? mergedStepNameMap[stepFilter] || steps.find(s => s.id === stepFilter)?.name : null;

    // Check if we're in initial loading state (no data yet)
    const isInitialLoading = isLoading && !tracesData;


    return (
        <div className={cn('flex flex-col h-full', className)}>
            {/* Header — compact toolbar: live count + search + a single Filters
                popover. Everything else collapses into removable chips below. */}
            <div className="flex-shrink-0 px-3 py-2.5 border-b bg-background">
                <div className="flex items-center gap-2">
                    {/* Left: count + live status */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1.5 text-sm">
                            <Users className="h-3.5 w-3.5 text-muted-foreground" />
                            {isInitialLoading ? (
                                <Skeleton className="h-4 w-8" />
                            ) : (
                                <>
                                    <span className="font-semibold tabular-nums">{total}</span>
                                    {(debouncedSearch || hasActiveFilters) && summary.totalSessions !== total && (
                                        <span className="text-muted-foreground text-xs font-normal">
                                            / {summary.totalSessions}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                        {/* Converted / dropped breakdown — desktop only to keep phones calm */}
                        {!isInitialLoading && !hasActiveFilters && (
                            <div className="hidden items-center gap-1.5 sm:flex">
                                <Badge variant="secondary" className="h-5 px-1.5 gap-1 text-[10px] font-medium bg-green-500/10 text-green-600 border-green-500/20">
                                    <CheckCircle2 className="h-2.5 w-2.5" />
                                    {summary.convertedSessions}
                                </Badge>
                                <Badge variant="secondary" className="h-5 px-1.5 gap-1 text-[10px] font-medium">
                                    <XCircle className="h-2.5 w-2.5" />
                                    {summary.droppedSessions}
                                </Badge>
                            </div>
                        )}
                        {/* Loading indicator for refetch */}
                        {isFetching && !isInitialLoading && (
                            <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />
                        )}
                        {/* Live polling indicator */}
                        {isPollingEnabled && page === 0 && !debouncedSearch && !isFetching && (
                            <button
                                onClick={() => setIsPollingEnabled(!isPollingEnabled)}
                                className="flex items-center gap-1 text-[10px] text-green-600 hover:text-green-700 transition-colors"
                                title="Live updates enabled (click to toggle)"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                </span>
                                Live
                            </button>
                        )}
                        {!isPollingEnabled && page === 0 && !debouncedSearch && (
                            <button
                                onClick={() => setIsPollingEnabled(true)}
                                className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                                title="Live updates paused (click to enable)"
                            >
                                <Wifi className="h-2.5 w-2.5" />
                                Paused
                            </button>
                        )}
                    </div>

                    {/* Right: search (grows) + Filters popover */}
                    <div className="flex flex-1 items-center justify-end gap-2 min-w-0">
                        {/* Search Input */}
                        <div className="relative min-w-0 flex-1 max-w-[260px]">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search email, ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-8 w-full pl-7 pr-6 text-xs"
                            />
                            {searchQuery && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0.5 top-1/2 -translate-y-1/2 h-5 w-5 p-0 hover:bg-transparent"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            )}
                        </div>

                        {/* Filters popover — folds step / status / device / country / sort */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 flex-shrink-0 gap-1.5 px-2.5 text-xs">
                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">Filters</span>
                                    {popoverFilterCount > 0 && (
                                        <span className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                                            {popoverFilterCount}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-60 p-3">
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] text-muted-foreground">Reached step</Label>
                                        <Select value={stepFilter || 'all'} onValueChange={handleStepFilterChange}>
                                            <SelectTrigger className="h-8 w-full text-xs">
                                                <Footprints className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                                <SelectValue placeholder="Reached step" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all" className="text-xs">All sessions</SelectItem>
                                                {steps.map((step, idx) => (
                                                    <SelectItem key={step.id} value={step.id} className="text-xs">
                                                        Reached {idx + 1}. {step.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] text-muted-foreground">Status</Label>
                                        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as SessionStatus); setPage(0); }}>
                                            <SelectTrigger className="h-8 w-full text-xs">
                                                <Filter className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all" className="text-xs">All</SelectItem>
                                                <SelectItem value="converted" className="text-xs">Converted</SelectItem>
                                                <SelectItem value="dropped" className="text-xs">Dropped</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] text-muted-foreground">Device</Label>
                                        <Select value={deviceTypeFilter || 'all'} onValueChange={(v) => setDeviceTypeFilter(v === 'all' ? null : v)}>
                                            <SelectTrigger className="h-8 w-full text-xs">
                                                <Smartphone className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                                <SelectValue placeholder="Device" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all" className="text-xs">All</SelectItem>
                                                <SelectItem value="mobile" className="text-xs">Mobile</SelectItem>
                                                <SelectItem value="desktop" className="text-xs">Desktop</SelectItem>
                                                <SelectItem value="tablet" className="text-xs">Tablet</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] text-muted-foreground">Country</Label>
                                        <Select value={countryFilter || 'all'} onValueChange={(v) => setCountryFilter(v === 'all' ? null : v)}>
                                            <SelectTrigger className="h-8 w-full text-xs">
                                                <Globe2 className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                                <SelectValue placeholder="Country" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all" className="text-xs">All</SelectItem>
                                                <SelectItem value="US" className="text-xs">🇺🇸 US</SelectItem>
                                                <SelectItem value="FR" className="text-xs">🇫🇷 FR</SelectItem>
                                                <SelectItem value="DE" className="text-xs">🇩🇪 DE</SelectItem>
                                                <SelectItem value="ES" className="text-xs">🇪🇸 ES</SelectItem>
                                                <SelectItem value="IT" className="text-xs">🇮🇹 IT</SelectItem>
                                                <SelectItem value="CA" className="text-xs">🇨🇦 CA</SelectItem>
                                                <SelectItem value="GB" className="text-xs">🇬🇧 UK</SelectItem>
                                                <SelectItem value="AU" className="text-xs">🇦🇺 AU</SelectItem>
                                                <SelectItem value="NL" className="text-xs">🇳🇱 NL</SelectItem>
                                                <SelectItem value="BE" className="text-xs">🇧🇪 BE</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <Separator />

                                    <div className="space-y-1.5">
                                        <Label className="text-[11px] text-muted-foreground">Sort by</Label>
                                        <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'started_at' | 'ended_at')}>
                                            <SelectTrigger className="h-8 w-full text-xs">
                                                <ArrowUpDown className="h-3 w-3 mr-1.5 text-muted-foreground" />
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="started_at" className="text-xs">Started</SelectItem>
                                                <SelectItem value="ended_at" className="text-xs">Last Activity</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {hasActiveFilters && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-full justify-center text-xs text-muted-foreground hover:text-foreground"
                                            onClick={clearAllFilters}
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear all filters
                                        </Button>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                {/* Active filter chips — one removable pill per applied filter */}
                {(statusFilter !== 'all' || stepFilter || deviceTypeFilter || countryFilter) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {statusFilter !== 'all' && (
                            <FilterChip
                                icon={statusFilter === 'converted'
                                    ? <CheckCircle2 className="h-3 w-3 text-green-600" />
                                    : <XCircle className="h-3 w-3 text-muted-foreground" />}
                                label={statusFilter === 'converted' ? 'Converted' : 'Dropped'}
                                onClear={() => { setStatusFilter('all'); setPage(0); }}
                            />
                        )}
                        {stepFilter && selectedStepName && (
                            <FilterChip
                                icon={<Footprints className="h-3 w-3 text-primary" />}
                                label={!isInitialLoading ? `${selectedStepName} (${total})` : selectedStepName}
                                onClear={clearStepFilter}
                            />
                        )}
                        {deviceTypeFilter && (
                            <FilterChip
                                icon={<Smartphone className="h-3 w-3 text-muted-foreground" />}
                                label={deviceTypeFilter.charAt(0).toUpperCase() + deviceTypeFilter.slice(1)}
                                onClear={() => setDeviceTypeFilter(null)}
                            />
                        )}
                        {countryFilter && (
                            <FilterChip
                                icon={<Globe2 className="h-3 w-3 text-muted-foreground" />}
                                label={`${getCountryFlag(countryFilter)} ${countryFilter}`.trim()}
                                onClear={() => setCountryFilter(null)}
                            />
                        )}
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            className="ml-0.5 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Session List - Scrollable */}
            <div className="flex-1 overflow-y-auto">
                {/* Initial loading skeleton */}
                {isInitialLoading ? (
                    <div className="p-3 space-y-2">
                        {[...Array(8)].map((_, i) => (
                            <Skeleton key={i} className="h-14 w-full rounded-lg" />
                        ))}
                    </div>
                ) : traces.length === 0 ? (
                    <div className="h-64 flex flex-col items-center justify-center text-muted-foreground gap-3">
                        <div className="p-3 rounded-full bg-muted/50">
                            <Users className="h-8 w-8 text-muted-foreground/50" />
                        </div>
                        <div className="text-center space-y-1">
                            {searchQuery ? (
                                <>
                                    <p className="text-sm font-medium">No sessions match "{searchQuery}"</p>
                                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setSearchQuery('')}>
                                        Clear search
                                    </Button>
                                </>
                            ) : stepFilter ? (
                                <p className="text-sm">No sessions reached "{selectedStepName}"</p>
                            ) : (
                                <p className="text-sm">No sessions found in this period</p>
                            )}
                        </div>
                    </div>
                ) : (
                    // Render session list - non-virtualized for simplicity with expandable items
                    <div className="space-y-2 p-3">
                        <AnimatePresence mode="popLayout">
                            {traces.map((session) => {
                                const isNew = newSessionIds.has(session.sessionId);
                                return (
                                    <motion.div
                                        key={session.sessionId}
                                        layout
                                        initial={isNew ? { opacity: 0, y: -20, scale: 0.95 } : false}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ 
                                            type: 'spring', 
                                            stiffness: 500, 
                                            damping: 30,
                                            mass: 1,
                                        }}
                                    >
                                        <Collapsible
                                            open={expandedSessionId === session.sessionId}
                                            onOpenChange={() => toggleSession(session.sessionId)}
                                        >
                                            <CollapsibleTrigger asChild>
                                                <div
                                                    className={cn(
                                                        'flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm',
                                                        session.status === 'converted'
                                                            ? 'bg-green-500/5 border-green-500/20 hover:bg-green-500/10'
                                                            : 'bg-card hover:bg-muted/40 border-border',
                                                        expandedSessionId === session.sessionId && 'ring-1 ring-primary/40 shadow-sm border-primary/40',
                                                        // Highlight new sessions with a pulse effect
                                                        isNew && 'ring-2 ring-green-500/50 animate-pulse'
                                                    )}
                                                >
                                        <div className="flex items-center gap-3 min-w-0">
                                            {expandedSessionId === session.sessionId ? (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            )}
                                            
                                            {/* Status Icon */}
                                            {session.status === 'converted' ? (
                                                <div className="p-1 rounded-full bg-green-500/10">
                                                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                                                </div>
                                            ) : (
                                                <div className="p-1 rounded-full bg-muted">
                                                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                                                </div>
                                            )}

                                            {/* Customer Info */}
                                            <div className="flex flex-col min-w-0 gap-0.5">
                                                <div className="flex items-center gap-1.5">
                                                    {session.isPreview && (
                                                        <Badge variant="yellow" className="h-4 gap-0.5 px-1 text-[9px] font-normal">
                                                            <FlaskConical className="h-2.5 w-2.5" />
                                                            Preview
                                                        </Badge>
                                                    )}
                                                    {/* Note: Variant badges are shown at event level only (not session level) 
                                                        because different steps can have different variants */}
                                                    <span className="text-sm font-medium truncate text-foreground">
                                                        {session.customerEmail || `Session ${session.sessionId.slice(-8)}`}
                                                    </span>
                                                    {/* Customer Tags */}
                                                    {session.customerTags && session.customerTags.length > 0 && (
                                                        <div className="flex items-center gap-1">
                                                            {session.customerTags.slice(0, 2).map((tag) => (
                                                                <Badge 
                                                                    key={tag} 
                                                                    variant="outline" 
                                                                    className="h-4 px-1 text-[9px] font-normal text-muted-foreground"
                                                                >
                                                                    {tag}
                                                                </Badge>
                                                            ))}
                                                            {session.customerTags.length > 2 && (
                                                                <span className="text-[9px] text-muted-foreground">+{session.customerTags.length - 2}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                    {formatDistanceToNow(new Date(session.startedAt), { addSuffix: true })}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            {/* Device / Country / Browser - grouped with visual separator */}
                                            <div className="flex items-center gap-1.5">
                                                {/* Device Type */}
                                                {session.deviceType && (
                                                    <DeviceTypeIcon deviceType={session.deviceType} className="h-3.5 w-3.5 text-muted-foreground" />
                                                )}
                                                {/* Country Flag */}
                                                {session.country && (
                                                    <span className="text-sm leading-none" title={session.country}>
                                                        {getCountryFlag(session.country)}
                                                    </span>
                                                )}
                                                {/* Browser */}
                                                {session.browser && (
                                                    <span className="flex items-center relative">
                                                        <BrowserIcon browser={session.browser} className="h-3.5 w-3.5" />
                                                        {isInAppBrowser(session.browser) && (
                                                            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 border border-background" />
                                                        )}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Duration */}
                                            <span className="flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                                                <Clock className="h-3 w-3 flex-shrink-0" />
                                                {formatDuration(session.durationSeconds)}
                                            </span>

                                            {/* Event count — number of tracked events in this session */}
                                            <span
                                                className="flex items-center gap-0.5 text-[11px] text-muted-foreground tabular-nums"
                                                title={`${session.eventCount} events`}
                                            >
                                                <Footprints className="h-3 w-3 flex-shrink-0" />
                                                {session.eventCount}
                                            </span>

                                            {/* Drop-off step */}
                                            {session.status === 'dropped' && session.dropOffStepId && (
                                                <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-medium text-amber-700 border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800 max-w-[100px] truncate">
                                                    @ {mergedStepNameMap[session.dropOffStepId]?.slice(0, 12) || '?'}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </CollapsibleTrigger>

                                <CollapsibleContent className="data-[state=open]:animate-none data-[state=closed]:animate-none">
                                    <ExpandedSessionContent
                                        session={session}
                                        storeId={storeId}
                                        orgRoutes={orgRoutes}
                                    />
                                </CollapsibleContent>
                                        </Collapsible>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Pagination - Fixed at bottom */}
            {total > pageSize && (
                <div className="flex-shrink-0 flex items-center justify-between h-9 px-3 border-t bg-muted/20">
                    <span className="text-xs text-muted-foreground tabular-nums">
                        {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
                    </span>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs font-normal"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0 || isFetching}
                        >
                            ← Prev
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs font-normal"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!hasMore || isFetching}
                        >
                            Next →
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

